import { useCallback, useEffect, useState } from 'react';
import { Link, Typography } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CustomerService } from '../../services/CustomerService';
import { formatDateTime } from '../../utils/DateTimeUtils';

interface InputProps {
    userPhone: string;
}

const customerService = new CustomerService();

const COLUMNS: CommonListColumn[] = [
    { label: 'Id', field: 'id' },
    {
        label: 'From',
        render: (row) => row?.admin?.name || '-',
    },
    {
        label: 'To',
        minWidth: 180,
        render: (row) =>
            [row?.user?.fname, row?.user?.lname].filter(Boolean).join(' ') || row?.to || '-',
    },
    { label: 'Status', field: 'status', type: 'status' },
    {
        label: 'Duration',
        render: (row) => {
            const dur = row?.duration ?? 0;
            const mins = Math.floor(dur / 60);
            const secs = dur % 60;
            return mins !== 0 ? `${mins} min ${secs} sec` : `${secs} sec`;
        },
    },
    {
        label: 'Call Start Time',
        minWidth: 170,
        render: (row) => formatDateTime(row?.callStartTime) || '-',
    },
    {
        label: 'Call End Time',
        minWidth: 170,
        render: (row) => formatDateTime(row?.callEndTime) || '-',
    },
    {
        label: 'Reference Id',
        minWidth: 130,
        render: (row) => {
            const route =
                row?.referenceModelName === 'ONE_MEAL_SERVICE' ? 'OneMealService' : 'ChefForParty';
            return (
                <Typography>
                    <Link href={`/#/${route}/${row?.referenceId}/show`}>{row?.referenceId}</Link>
                </Typography>
            );
        },
    },
    {
        label: 'Reference Modal',
        minWidth: 170,
        render: (row) => row?.referenceModelName || '-',
    },
    {
        label: 'Purpose',
        render: (row) => row?.purpose || '-',
    },
    {
        label: 'Action',
        minWidth: 200,
        stopRowClick: true,
        render: (row) => <audio src={row?.recordingUrl} controls />,
    },
];

function CustomerCallLogs({ userPhone }: InputProps) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        customerService
            .getCustomerLogs(rowsPerPage, rowsPerPage * page, { order: 'DESC', field: 'createdAt' }, userPhone)
            .then((res: any) => {
                setData(res?.data?.data ?? []);
                setTotal(res?.data?.count ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [userPhone, page, rowsPerPage]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    return (
        <CommonListTable
            columns={COLUMNS}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyText="No Logs Found"
        />
    );
}

export default CustomerCallLogs;
